import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { CoveragesByDayDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import WrapperCell from 'modules/shift_management/components/ShiftManagement/Content/Body/DataTable/Cells/WrapperCell'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import CoveragesByDay from 'modules/shift_management/store/internal/collections/CoveragesByDay'

import { ColumnsType } from '../utils/buildColumns'
import EmployeesByDayGrid from './EmployeesByDayGrid'

interface Props {
  columns: ColumnsType
}

const EmployeesByDayRow = ({ columns }: Props) => {
  const { company } = useCurrentSession()
  const [filters] = useFilters()
  const [{ view, newShiftsToQuery }] = useSchedule()

  const employeeIds = filters.employee_ids?.map((employeeId) => Number(employeeId))

  const [open, toggle] = useToggle(false)
  const cells = columns.slice(1)
  const title = i18n.t('shift_management.employees_by_day.label')
  const tooltip = i18n.t('shift_management.employees_by_day.tooltip')

  const { data, refetch } = useQuery(CoveragesByDayDocument, {
    variables: {
      companyId: company.id,
      startAt: view.startAt,
      endAt: view.endAt,
      customFilters: filters.custom_filters,
      logicOperator: filters.logic_operator,
      employeeIds: employeeIds,
      terminated: filters.terminated,
      shiftIds: filters.shift_ids,
    },
  })

  const coveragesByDay: CoveragesByDay = useUnsafeMemo(
    () =>
      CoveragesByDay.buildFromRequest(data?.shiftManagement?.coveragesByDaysConnection.nodes || []),
    [data?.shiftManagement?.coveragesByDaysConnection]
  )

  useUnsafeEffect(() => {
    refetch()
  }, [refetch, newShiftsToQuery])

  return (
    <>
      <WrapperCell sticky minWidth="s240" height="s24">
        <Box
          background="grey000White"
          flexDirection="row"
          justifyContent="spaceBetween"
          height="full"
          padding="s8"
          alignItems="center"
        >
          <Tooltip.ItemList attachment="top center" open={open} toggle={toggle}>
            <Box flexDirection="row" alignItems="center" overflowX="hidden">
              <Text size="100" weight="bold" color="grey700" ellipsis>
                {title.toUpperCase()}
              </Text>
            </Box>

            <Box alignItems="center">
              <Text.Normal>{tooltip}</Text.Normal>
            </Box>
          </Tooltip.ItemList>
        </Box>
      </WrapperCell>
      <EmployeesByDayGrid coveragesByDay={coveragesByDay} columns={cells} />
    </>
  )
}

export default observer(EmployeesByDayRow)
